import { useCallback, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";

import { AiSummaryStatus, TranscriptionResult } from "../api/transcription.api";
import { useHistoryStore } from "../store/history.store";

// БЕЗОПАСНОСТЬ: WebSocket использует JWT аутентификацию через cookies + WSS (HTTPS)
// Для REST API используется VITE_API_BASE_URL с /api
// Для WebSocket нужен базовый URL БЕЗ /api (socket.io сам добавляет /socket.io)
const getWebSocketURL = () => {
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8050";
  return apiBaseUrl.replace(/\/api$/, "");
};

const BACKEND_URL = getWebSocketURL();

interface TranscriptionStatusChangedEvent {
  transcriptionId: string;
  status: string;
  transcription: TranscriptionResult;
}

interface SummaryReadyEvent {
  transcriptionId: string;
  aiSummary: TranscriptionResult["aiSummary"] | null;
  aiSummaryStatus: AiSummaryStatus;
}

interface TranscriptionFailedEvent {
  transcriptionId: string;
  errorMessage: string;
}

/**
 * Хук для подключения к WebSocket для реал-тайм обновлений транскрипций
 */
export const useTranscriptionWebSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const updateTranscriptionInCache = useHistoryStore(state => state.updateTranscriptionInCache);

  const connect = useCallback(() => {
    // Если сокет уже подключен, не создаем новый
    if (socketRef.current?.connected) {
      return;
    }

    // Очищаем предыдущий сокет если он есть
    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
    }

    // На проде используем ТОЛЬКО WebSocket (статус 101)
    // Polling создает много HTTP запросов со статусом 200 и "Finished"
    // REST запросы работают отдельно через обычный HTTP
    const socket = io(`${BACKEND_URL}/transcription`, {
      withCredentials: true,
      transports: ["websocket"], // Только WebSocket, без polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity
    });

    socketRef.current = socket;

    // Обработчик успешного подключения
    socket.on("connect", () => {
      // Подписываемся на обновления транскрипций
      socket.emit("transcription:subscribe");

      useHistoryStore.getState().signalWsResync();
    });

    // Обработчик ping от сервера (heartbeat)
    socket.on("ping", () => {
      // Автоматически отвечаем pong
      socket.emit("pong");
    });

    // Обработчик изменения статуса транскрипции
    socket.on("transcription:status_changed", (event: TranscriptionStatusChangedEvent) => {
      updateTranscriptionInCache(event.transcriptionId, event.transcription);
    });

    // Обработчик завершения транскрипции
    socket.on("transcription:completed", (transcription: TranscriptionResult) => {
      // Приходит полный результат — помечаем, чтобы карточка не запрашивала деталь повторно
      updateTranscriptionInCache(transcription.id, { ...transcription, isDetailLoaded: true });
    });

    // Авто-сводка готова (или не получилась) — приходит уже после
    // transcription:completed, поэтому обновляем только поля сводки
    socket.on("transcription:summary_ready", (event: SummaryReadyEvent) => {
      updateTranscriptionInCache(event.transcriptionId, {
        id: event.transcriptionId,
        aiSummary: event.aiSummary ?? undefined,
        aiSummaryStatus: event.aiSummaryStatus
      });
    });

    // Обработчик провала транскрипции
    socket.on("transcription:failed", (event: TranscriptionFailedEvent) => {
      updateTranscriptionInCache(event.transcriptionId, {
        id: event.transcriptionId,
        status: "failed",
        errorMessage: event.errorMessage
      } as TranscriptionResult);
    });

    // Обработчик ошибки подключения
    socket.on("connect_error", error => {
      // Автоматическое переподключение через 3 секунды
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      reconnectTimeoutRef.current = setTimeout(() => {
        socket.connect();
      }, 3000);
    });

    // Обработчик отключения
    socket.on("disconnect", reason => {
      // Переподключаемся для любого типа разрыва кроме явного disconnect клиента
      if (reason !== "io client disconnect") {
        socket.connect();
      }
    });

    return socket;
  }, [updateTranscriptionInCache]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (socketRef.current) {
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Подключаемся при монтировании
    connect();

    // Отключаемся при размонтировании
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    socket: socketRef.current,
    connect,
    disconnect,
    isConnected: socketRef.current?.connected || false
  };
};
